/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **`Shared…` is a picker, not a save button** — `X28a`, `XS3` slice 4 (#1222).
 *
 * At row level the useful act is *reach for one that already exists*. Saving **this** value into the
 * library is what happens once there is something worth naming, and it belongs in the expression
 * editor where the authoring happened — which is where it already is (`DecoratorExpressionEditor`'s
 * *Save expression to library*). The canvas's shot 05 had it backwards, offering *Save as shared*
 * beside a bare context path: an offer to publish nothing, on the surface least likely to want it.
 *
 * `X28a`'s argument for the direction is the one worth keeping: **a library that is easy to draw from
 * accumulates addresses, while one that is easy to push into accumulates duplicates.**
 *
 * ## It fetches its own list, and that is deliberate
 *
 * The library is global reference data rather than per-surface state, so this reads it directly
 * (`useExpressionLibrary`), the way `FieldPickerPanel` reads the field catalog. Threading it as a
 * prop through four editors and their pages would put the same list behind four chances to forget —
 * which is exactly the `#1134` failure mode `MappingControlProps` was made required to close.
 *
 * What stays a **capability** is whether the surface may use it at all (`offersLibrary`), because
 * that is a per-surface answer and one surface says no.
 *
 * ## Picking inserts an **address** (`XS5`, `#1066`)
 *
 * Until `XS5` this inserted a **copy** and said so on the surface, because nothing in
 * `MacroExpression` could hold a reference — a picker that looks like an address and behaves like a
 * copy is the #1045 defect inside the control added to improve reuse, so the honest thing was to
 * state it.
 *
 * It now hands up a {@link ReferenceExpression}, so editing the library reaches every row that
 * addressed it and *"three places use this"* is answerable. The sentence that stood in for it is
 * gone, and the surface it was on did not have to move — which is what it was written to allow.
 *
 * ## What it must never be able to say
 *
 * > *"There are no shared expressions."*
 *
 * Not while the list is loading and not when the request failed — `UsedByPanel`'s rule, for the same
 * reason: an operator told the library is empty stops looking, and goes and writes the expression
 * again. Each outcome renders as **what it is**.
 */

import React from 'react';
import { Box, Button, chakra, HStack, Text, VStack } from '@chakra-ui/react';

import { useExpressionLibrary } from '../../../hooks/useExpressionLibrary';
import { isSelectable } from '../../../utils/admin-metadata';
import { NO_DESCRIPTION_MARK } from '../../../utils/expression-library-rules';
import type { MacroExpression } from '../../../types/decorator-expression';
import type { SharedExpression } from '../../../types/expression-library';
import { summarizeMacroExpression } from './DecoratorExpressionEditor';
import { referenceTo } from './value-line';
import { describeUnusableHere, type ResolveSurfaces } from './shared-expression-usability';
import { isRefused, type CapabilityRefused } from './MappingControl';

/**
 * **A row is not an `HStack as="button"`.** Chakra's stack props do not include `type`, and a button
 * without `type="button"` inside a form submits it. `CapabilityChipRow` records the same finding for
 * the same reason: `chakra('button')` takes the style props verbatim and stays a real button.
 */
const Row = chakra('button');

export interface SharedExpressionPickerProps {
  /** Whether this surface may draw from the library, or why it may not. */
  capability: true | CapabilityRefused | null;
  /**
   * **What this surface's values resolve against** (`X18`), so an expression reading something the
   * surface does not have can be marked. `null` makes no claim and marks nothing.
   */
  resolvesAgainst: ResolveSurfaces | null;
  onPick: (expression: MacroExpression) => void;
  onCancel: () => void;
}

export const SharedExpressionPicker: React.FC<SharedExpressionPickerProps> = ({
  capability,
  resolvesAgainst,
  onPick,
  onCancel,
}) => {
  const refused = isRefused(capability) ? capability : null;
  const { data, isLoading, isError } = useExpressionLibrary();

  const available = React.useMemo(() => (data ?? []).filter((e: SharedExpression) => isSelectable(e)), [data]);

  return (
    <Box borderWidth="1px" borderColor="border.default" borderRadius="md" bg="bg.card" p={3}>
      <HStack justify="space-between" mb={2}>
        <Text fontSize="xs" fontWeight="600" color="fg.default">
          Shared expressions
        </Text>
        <Button size="xs" variant="ghost" onClick={onCancel}>
          Close
        </Button>
      </HStack>

      {refused ? (
        // Pattern 24: the reason is on the surface, never behind the hover (`X28b`).
        <Text fontSize="xs" color="fg.subtle">
          {refused.reason}
        </Text>
      ) : isLoading ? (
        <Text fontSize="xs" color="fg.muted">
          Looking…
        </Text>
      ) : isError ? (
        <Text fontSize="xs" color="warning.ink">
          Could not read the library. Nothing is claimed about what is in it.
        </Text>
      ) : available.length === 0 ? (
        <Text fontSize="xs" color="fg.muted">
          Nothing in the library yet. Build the value here, then save it from the expression editor below.
        </Text>
      ) : (
        <VStack align="stretch" gap={1}>
          {available.map((entry) => {
            /**
             * **Offered and marked, never hidden** (`X18`, and `X32` question 1 applied to a list).
             *
             * Hiding an expression that cannot resolve here answers by silence, and the operator's
             * next move is to author it again by hand and get the same nothing. It stays pickable
             * too: this is a claim about **where the value comes from**, not a validation, and an
             * operator building a surface up may well want it there before the thing it reads
             * exists. The sentence is what they need; the block is not.
             */
            const unusable = describeUnusableHere(entry.expression, resolvesAgainst);
            return (
              <Row
                key={entry.id}
                type="button"
                /* **The address, not the expression** (`X5`, `XS5`). The row stores which expression
                 it uses; the config read hands the runtime what it names. */
                onClick={() => onPick(referenceTo(entry))}
                display="flex"
                flexDirection="column"
                alignItems="stretch"
                gap={0.5}
                textAlign="left"
                px={2}
                py={1.5}
                borderRadius="sm"
                borderWidth="1px"
                borderColor="border.subtle"
                _hover={{ borderColor: 'accent.solid', bg: 'bg.subtle' }}
              >
                <Text fontSize="sm" fontWeight="600" color="fg.default">
                  {entry.name}
                </Text>
                {/* **The second line is the author's own sentence** — `X28a`/`X44`, and it is the whole
                  reason the library makes a description required (`#1273`). A required field nothing
                  reads is the *offered name with no writer* family inverted, so this surface is where
                  that requirement is paid off. An entry saved before the rule still renders honestly
                  rather than leaving a blank the operator has to interpret. */}
                <Text fontSize="xs" color={entry.description ? 'fg.muted' : 'fg.subtle'} truncate>
                  {entry.description || NO_DESCRIPTION_MARK}
                </Text>
                {/* **And what it does mechanically**, in the editor's own words.
                  `summarizeMacroExpression` is what the collapsed row renders, so the library and the
                  row cannot describe one expression two ways (`#1187` — a duplicated explanation
                  yields a wrong sentence nothing checks). */}
                <Text fontSize="10px" color="fg.muted" fontFamily="mono" truncate>
                  {summarizeMacroExpression(entry.expression)}
                </Text>
                {/* **A negative claim, and only ever a negative one** (`E1`). *Usable here* is not
                  something this can say — a path can resolve and still be the wrong value — so the
                  absence of this line means nothing was found against it, not that it works. */}
                {unusable && (
                  <Text fontSize="10px" color="warning.ink">
                    {unusable}
                  </Text>
                )}
              </Row>
            );
          })}
          {/* **What picking one actually does**, which is now a different sentence from the one this
              surface carried through `XS3` — and the reason it is still a sentence: an operator
              reaching for reuse is deciding whether a later edit reaches them, and that is the
              answer they need before they pick, not after. */}
          <Text fontSize="xs" color="fg.subtle" mt={1}>
            This row will use the expression you pick. Editing it in the library changes it everywhere it is used,
            including here.
          </Text>
        </VStack>
      )}
    </Box>
  );
};
